
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Polygon from "@arcgis/core/geometry/Polygon";
import Point from "@arcgis/core/geometry/Point";
import Polyline from "@arcgis/core/geometry/Polyline";
import Graphic from "@arcgis/core/Graphic";
import PropTypes from 'prop-types';
import { emitCustomEvent, useCustomEventListener } from 'react-custom-events';
import Events from '../../defaults/Events';
import {MutableRefObject, useEffect, useRef, useState} from "react";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import * as projection from "@arcgis/core/geometry/projection";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import symbols from '../../defaults/Symbols.json';
import {getPolygonSymbol} from "./MapEventHelper";
import FieldNames from "../../defaults/FieldNames.json";
import type {DrawToolbarEventData} from "../../types/esri-extensions.tsx";

function DrawToolbar({view, modifiedGraphicsLayer}) {

  const [sketchViewModel, setSketchViewModel] = useState<null | SketchViewModel>(null);
  let isMovingPoint = false;
  let isUploading = false;
  let shouldUpdateGraphic = true;
  const sketchGraphicsLayer: MutableRefObject<null | GraphicsLayer> = useRef(null);
  let data: MutableRefObject<null | DrawToolbarEventData> = useRef(null);

  //Setup sketchviewmodel once we have a view and a graphicslayer
  useEffect(() => {
    if (!view || !sketchGraphicsLayer)
    {
      return;
    }

    if(sketchViewModel)
    {
      return;
    }
    createSketchViewModel(view);
  }, [view, sketchGraphicsLayer]); //, sketchViewModel

  const onCreatePoint = (isFuelsPoint, args) => {
      // ONLY new fuels activity points are buffered. (fuels treatment points not currently possible). All other are saved as points.
      const geom = (isFuelsPoint) ? sketchBufferedPoint(args) : sketchNonBufferedPoint(args);
      data.current?.callback(geom, data.current?.callbackParams);
  }
  
  const sketchNonBufferedPoint = (args) => {
    const point = new Point(args.graphic.geometry);
    const pointGraphic = new Graphic({
      geometry: point,
      symbol: symbols.pointSymbolMod,
    });
    sketchGraphicsLayer.current?.add(pointGraphic);
    emitCustomEvent(Events.ModifiedGraphicPostFirePointChanged, pointGraphic);
    return point;
  }

  const sketchBufferedPoint = (args) => {
    const ptBuff = geometryEngine.geodesicBuffer(args.graphic.geometry, 7000, "feet") as Polygon;
    if(isMovingPoint) {
      const polyGraphic = new Graphic({
        geometry: Polygon.fromJSON(ptBuff.toJSON()),
        symbol: {
          type: "simple-fill",
          color: "rgba(138,43,226, 0.8)",
          style: "solid",
          outline: {
            color: "black",
            width: 1
          }
        }
      });
      sketchGraphicsLayer.current?.add(polyGraphic);
      emitCustomEvent(Events.ModifiedGraphicChanged, polyGraphic);
    }
    return ptBuff;
  }

  const createSketchViewModel = (view) => {
    const gl = new GraphicsLayer({
      view: view,
      listMode: 'hide',
      title: 'sketch-graphics-layer'
    })
    sketchGraphicsLayer.current = gl;

    const svm = new SketchViewModel({
      view: view,
      layer: gl,
      updateOnGraphicClick: true,
      defaultUpdateOptions:
      {
        toggleToolOnClick: false
      }
    });
    setSketchViewModel(svm);

    svm.on('create', (args) => {
      if (args.state === "complete" && args.graphic && args.graphic.geometry) {
        // Only new fuels activities (treatment points currently not possible) are buffered. All postfire points are saved as points.
        const isFuelsPoint = !data.current?.callbackParams?.isPostFire
        if (args.graphic.geometry.type === 'point') {
            onCreatePoint(isFuelsPoint, args);
        } else if (data.current?.callback) {
          data.current?.callback(args.graphic.geometry, data.current?.callbackParams);
        }
      }
    });
    svm.on("update", (event) => {
      onGraphicUpdate(event);
      onLineGraphicUpdate(event);
    });
  }


  useCustomEventListener(Events.DisableDrawMode, () => {
    sketchViewModel?.cancel();
    // sketchViewModel?.complete();
    sketchViewModel?.layer.graphics.removeAll();
    emitCustomEvent(Events.ClearModifiedLayer);
  });

  useCustomEventListener(Events.CompleteDrawMode, () => {
    sketchViewModel?.complete();
    onGraphicUpdateComplete();
  });

  //Draw a polygon when someone tells us to.
  useCustomEventListener(Events.EnableDrawMode, (eventData: DrawToolbarEventData) => {
    data.current = eventData;
    if (data.current && data.current?.attributes?.isPoint !== FieldNames.yesNo.yes) {
      view.popup.visible = false;
      if (data.current?.geometry instanceof Polygon) {
        const perimeter = new Polygon({
          rings: data.current?.geometry?.rings,
          spatialReference: new SpatialReference( {
            wkid: 4269
          })
        });

        return projection.load().then(function() {
          const mercatorGeometry = projection.project(perimeter, new SpatialReference({wkid: 102100 }));
          let defaultSymbol = {
                type: "simple-fill",
                color: "rgba(138,43,226, 0.8)",
                style: "solid",
                outline: {
                    color: "black",
                    width: 1
                }
            }
          let symbol = (data.current?.attributes && data.current?.attributes[FieldNames.featureFields.class]) ?  getPolygonSymbol(data.current?.attributes[FieldNames.featureFields.class] ) : defaultSymbol;
          symbol.outline = {
            style: "long-dash-dot",
            color: "cyan",
            width: 3
          }
          const polyGraphic = new Graphic({
            geometry: mercatorGeometry,
            symbol: symbol
          });

          sketchGraphicsLayer.current?.add(polyGraphic);
          sketchViewModel?.update(polyGraphic, { tool: "reshape" });
          if(data.current?.callback){
            data.current?.callback(polyGraphic, data.current?.callbackParams);
          }
          return polyGraphic;
        });
      }
      else {
        sketchViewModel?.create('polygon');
      }
    }
  });

  useCustomEventListener(Events.ToggleIsMovingPoint, (data: boolean) => {
    isMovingPoint = data;
  });

  useCustomEventListener(Events.GetIsMovingPoint, (done) => {
    done(isMovingPoint);
  });

  useCustomEventListener(Events.ToggleShouldUpdateGraphic, (data: boolean) => {
    shouldUpdateGraphic = data;
  });

  useCustomEventListener(Events.GetShouldUpdateGraphic, (done) => {
    done(shouldUpdateGraphic);
  });

  useCustomEventListener(Events.ClearSketchGraphicsLayer, () => {
    sketchGraphicsLayer.current?.graphics.removeAll();
  })

  useCustomEventListener(Events.EnablePointMode, (eventData: DrawToolbarEventData) => {
    data.current = eventData;
    view.popup.visible = false;
    sketchViewModel?.create('point');
  });

  useCustomEventListener(Events.EnablePostFireLineMode, (eventData: DrawToolbarEventData) => {
    data.current = eventData;
    view.popup.visible = false;
      if (data.current && data.current?.geometry) {
        const polyline = new Polyline(data.current?.geometry);
        projection.load().then(function() {
          const mercatorGeometry = projection.project(polyline, new SpatialReference({wkid: 102100 }));
          const lineGraphic = new Graphic({
            geometry: mercatorGeometry,
            symbol: symbols.lineSymbolMod,
          });
          sketchGraphicsLayer.current?.add(lineGraphic);
          sketchViewModel?.update([lineGraphic], { tool: "reshape" });
          if(data.current?.callback){
            data.current?.callback(lineGraphic, data.current?.callbackParams);
          }
        })

      }
      else {
        sketchViewModel?.create('polyline');
      }
  })

  useCustomEventListener(Events.EnablePostFirePointMode, (eventData: DrawToolbarEventData) => {
    data.current = eventData;
    view.popup.visible = false;
    sketchViewModel?.create('point');
  });

  useCustomEventListener(Events.UploadGeometry, () => {
    isUploading = true;
  });

  const onGraphicUpdate = (event) => {
    const graphic = event.graphics[0];
    if(!isUploading){
      if (shouldUpdateGraphic)
      {
        emitCustomEvent(Events.ModifiedGraphicChanged, graphic);
      }
    }
  };

  const onLineGraphicUpdate = (event) => {
    const graphic = event.graphics[0];
    if(!isUploading){
      if (shouldUpdateGraphic)
      {
        emitCustomEvent(Events.ModifiedGraphicLineChanged, graphic);
      }
    }
  };

  const onGraphicUpdateComplete = () => {
    if(isUploading){
      isUploading = false;
    }
    sketchViewModel?.complete();
    let sketchGraphic = sketchGraphicsLayer.current?.graphics.getItemAt(0)
    if(sketchGraphic){
        modifiedGraphicsLayer.graphics.removeAll();
        emitCustomEvent(Events.ModifiedGraphicChanged, sketchGraphicsLayer.current?.graphics.getItemAt(0));
        sketchGraphicsLayer.current?.graphics.removeAll();
    }
  }

  return null;
}

DrawToolbar.propTypes = {
  view: PropTypes.any,
}

export default DrawToolbar;
